import { PROGRAM_SEED, programSubtypeVocabulary } from '../programs/seed-data';
import { subtypePromptLabelForTag, subtypeTagForAnswer } from '../programs/subtype-vocabulary';
import {
  asNonEmptyString,
  asDisplayString,
  normaliseProgramKey,
  readAnswer,
  resolvePrimaryProgramKey,
  PROGRAM_ANSWER_KEYS,
} from '../programs/program-key';
import { onboardingSecondaryFocusSchema } from './schema';

/** Imported values retain their provenance and must belong to the selected program. */
export const LEGACY_ANSWER_PREFIXES = {
  goal: 'legacy_goal',
  motivation: 'legacy_motivation',
  acuity: 'legacy_acuity',
} as const;

type Answers = Record<string, unknown>;

/** Map a single enum answer to its human label; unknown values pass through as their raw string. */
function labelledOne(map: Record<string, string>, raw: unknown): string | undefined {
  const s = asNonEmptyString(raw);
  if (s === undefined) return undefined;
  return map[s] ?? s;
}

/**
 * Map an enum answer to its human label. A multi-select answer arrives as an
 * array of values; each is labelled and the resolvable labels are joined, so a
 * member who chose several reasons carries all of them into the prompt. A scalar
 * answer keeps its single label. An empty or fully-unresolvable value yields
 * undefined — a required variable then still fails the call closed.
 */
function labelled(map: Record<string, string>, raw: unknown): string | undefined {
  if (Array.isArray(raw)) {
    const labels = raw
      .map((item) => labelledOne(map, item))
      .filter((label): label is string => label !== undefined);
    return labels.length > 0 ? labels.join(', ') : undefined;
  }
  return labelledOne(map, raw);
}

const PROGRAM_LABELS: Record<string, string> = Object.fromEntries(
  PROGRAM_SEED.map((program) => [program.key, program.displayName]),
);

/**
 * A programme's label for the prompt. A programme that declares a sub-type
 * vocabulary is named by the member's sub-type answer (an intake answer value,
 * or a tag id carried over from an import) through that vocabulary, and by its
 * unspecified label when there is no usable answer. The raw answer is read here,
 * not the saved tag: intake agents run before enrollment writes any tag.
 */
function labelledProgram(raw: unknown, answers: Answers): string | undefined {
  if (Array.isArray(raw)) {
    const labels = raw
      .map((item) => labelledProgram(item, answers))
      .filter((label): label is string => label !== undefined);
    return labels.length > 0 ? labels.join(', ') : undefined;
  }
  const key = normaliseProgramKey(raw);
  if (key === undefined) return undefined;
  const vocabulary = programSubtypeVocabulary(key);
  if (vocabulary !== undefined) {
    const tag = subtypeTagForAnswer(vocabulary, readAnswer(answers, vocabulary.answer_key));
    const label = tag === undefined ? undefined : subtypePromptLabelForTag(vocabulary, tag);
    return label ?? vocabulary.unspecified_label;
  }
  return PROGRAM_LABELS[key] ?? key;
}

function labelledSecondaryFocus(raw: unknown, labels: Record<string, string>): string | undefined {
  return labelled(labels, raw);
}

/** One selection rule for native labels, primary program and imported fallbacks. */
function resolveNativeProgramSelection(answers: Answers): unknown {
  return PROGRAM_ANSWER_KEYS.map((key) => readAnswer(answers, key)).find(
    (value) => labelledProgram(value, answers) !== undefined,
  );
}

function resolveProgram(answers: Answers, labels: Record<string, string>): string | undefined {
  return (
    labelledProgram(resolveNativeProgramSelection(answers), answers) ??
    labelledSecondaryFocus(readAnswer(answers, 'secondary_focus'), labels) ??
    labelledSecondaryFocus(readAnswer(answers, 'condition'), labels)
  );
}

/**
 * The member's single primary program LABEL — the shared key-level clinical
 * priority resolution (`resolvePrimaryProgramKey`) mapped to its display name.
 * An empty selection falls back to the secondary focus.
 */
function resolveProgramPrimary(
  answers: Answers,
  labels: Record<string, string>,
): string | undefined {
  const key = resolvePrimaryProgramKey({ program_key: resolveNativeProgramSelection(answers) });
  if (key !== undefined) return labelledProgram(key, answers);
  return (
    labelledSecondaryFocus(readAnswer(answers, 'secondary_focus'), labels) ??
    labelledSecondaryFocus(readAnswer(answers, 'condition'), labels)
  );
}

/**
 * The member's optional behavioral focus, shown beside a primary program. A
 * focus that is the member's only selection already fills `program`, so it is
 * left empty here rather than rendered in both slots. Only the onboarding
 * screen's own options resolve: anything else stored under the key, including
 * free text, yields nothing, because this value is rendered into the prompt.
 */
function resolveBehavioralFocus(
  answers: Answers,
  labels: Record<string, string>,
): string | undefined {
  if (resolveNativeProgramSelection(answers) === undefined) return undefined;
  const parsed = onboardingSecondaryFocusSchema.safeParse(readAnswer(answers, 'secondary_focus'));
  return parsed.success ? labels[parsed.data] : undefined;
}

/**
 * Tier 0 preserves historical answers under program-scoped legacy keys. Read
 * them as fallbacks, never rewrite them as answers to the new intake. Both
 * goal and motivation must belong to the same primary program; a sparse card
 * cannot borrow another program's answer merely to get past admission.
 */
function resolveMigratedVariables(
  answers: Answers,
  labels: Record<string, string>,
): Record<string, string | undefined> {
  const legacyText = (field: keyof typeof LEGACY_ANSWER_PREFIXES, program: string) =>
    asDisplayString(readAnswer(answers, `${LEGACY_ANSWER_PREFIXES[field]}:${program}`));
  // Only a card carrying BOTH texts is a selection candidate: a partially
  // filled higher-priority card (for example acuity-only) must not shadow a
  // complete lower-priority card and lock the member out.
  const programs = PROGRAM_SEED.map((program) => program.key).filter(
    (key) => legacyText('goal', key) !== undefined && legacyText('motivation', key) !== undefined,
  );
  const nativeProgram = resolveProgram(answers, labels);
  const nativeSelection = resolveNativeProgramSelection(answers);
  const program =
    nativeProgram !== undefined
      ? resolvePrimaryProgramKey({ program_key: nativeSelection })
      : resolvePrimaryProgramKey({ program_key: programs });
  // A native secondary focus (for example gambling) must not inherit an
  // unrelated substance card. Unknown suffixes cannot establish selection.
  if (program === undefined || !programs.includes(program)) return {};
  return {
    program: labelledProgram(program, answers),
    program_primary: labelledProgram(program, answers),
    goal: legacyText('goal', program),
    motivation: legacyText('motivation', program),
  };
}

/** One resolution contract for migration admission and every agent's required answers. */
export function resolveOnboardingPersonalization(
  answers: Answers,
  labels: {
    goal?: Record<string, string>;
    motivation?: Record<string, string>;
    secondaryFocus?: Record<string, string>;
  } = {},
) {
  const secondaryFocus = labels.secondaryFocus ?? {};
  const migrated = resolveMigratedVariables(answers, secondaryFocus);
  const motivation = [
    labelled(labels.motivation ?? {}, readAnswer(answers, 'motivation')),
    asDisplayString(readAnswer(answers, 'motivation_other')),
  ]
    .filter((value): value is string => value !== undefined)
    .join(', ');
  return {
    program: resolveProgram(answers, secondaryFocus) ?? migrated.program,
    program_primary: resolveProgramPrimary(answers, secondaryFocus) ?? migrated.program_primary,
    goal: labelled(labels.goal ?? {}, readAnswer(answers, 'goal')) ?? migrated.goal,
    motivation: motivation || migrated.motivation,
    behavioral_focus: resolveBehavioralFocus(answers, secondaryFocus),
  };
}
